import { useEffect, useState } from "react";
import { PaperPlaneTilt, X, Users, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const TEMPLATES = [
  { k: "blank", l: "Free-form", subject: "", message: "" },
  {
    k: "notice", l: "Account notice",
    subject: "An update about your Sliply account",
    message: "We're reaching out with an important update about your Sliply account.\n\n[Add your message here]\n\nIf you have any questions, just reply to this email.",
  },
  {
    k: "reminder", l: "Scan reminder",
    subject: "Don't forget to scan your receipts 🧾",
    message: "Just a friendly nudge — you haven't scanned a receipt in a while!\n\nSnap your latest till slips to keep your spending insights up to date and earn more Moolas.",
  },
  {
    k: "reward", l: "Reward nudge",
    subject: "You've got Moolas waiting 🎉",
    message: "Thanks for being part of Sliply! You've been earning Moolas with every scan.\n\nHead to the Rewards page to see what you can redeem.",
  },
];

/**
 * Two modes:
 *  - Manual: pass recipients: [{ user_id, email, name }]
 *  - Group:  pass groupMode (fetches audience segments, sends by segment)
 */
export default function EmailComposeDialog({ open, recipients = [], groupMode = false, onClose }) {
  const [tpl, setTpl] = useState("blank");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Group mode
  const [segments, setSegments] = useState(null);
  const [segKey, setSegKey] = useState("all");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (open) {
      setTpl("blank"); setSubject(""); setMessage(""); setConfirming(false);
      if (groupMode) {
        setSegKey("all"); setSegments(null);
        api.get("/admin/email/segments").then((r) => setSegments(r.data)).catch(() => setSegments(false));
      }
    }
  }, [open, groupMode]);

  const applyTemplate = (k) => {
    setTpl(k);
    const t = TEMPLATES.find((x) => x.k === k);
    if (t && k !== "blank") { setSubject(t.subject); setMessage(t.message); }
  };

  const activeSeg = (segments || []).find((s) => s.key === segKey);
  const groupCount = activeSeg?.count ?? 0;

  const doSend = async (payload) => {
    setSending(true); setConfirming(false);
    try {
      const { data } = await api.post("/admin/email/send", payload);
      if (data.sent > 0) toast.success(`Sent to ${data.sent} user${data.sent === 1 ? "" : "s"}${data.failed?.length ? ` · ${data.failed.length} failed` : ""}`);
      else toast.error(`Could not send${data.failed?.[0]?.error ? `: ${data.failed[0].error}` : ""}`);
      if (data.failed?.length === 0) onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send emails.");
    } finally {
      setSending(false);
    }
  };

  const onSendClick = () => {
    if (!subject.trim() || !message.trim()) { toast.error("Add a subject and a message."); return; }
    if (groupMode) {
      if (groupCount === 0) { toast.error("No users match this group right now."); return; }
      setConfirming(true);
      return;
    }
    doSend({ user_ids: recipients.map((r) => r.user_id), subject: subject.trim(), message: message.trim() });
  };

  const confirmGroupSend = () => doSend({ segment: segKey, subject: subject.trim(), message: message.trim() });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="email-compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PaperPlaneTilt size={20} weight="duotone" className="text-riq-primary" />
            {groupMode ? "Email a group" : `Email ${recipients.length === 1 ? "user" : `${recipients.length} users`}`}
          </DialogTitle>
          <DialogDescription className="sr-only">Compose and send an email to users</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {groupMode ? (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1.5">Audience</div>
              {segments === false ? (
                <div className="text-sm text-red-500">Could not load audience groups.</div>
              ) : segments === null ? (
                <div className="text-sm text-muted-foreground">Loading groups…</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {segments.map((s) => (
                    <button
                      key={s.key}
                      data-testid={`email-segment-${s.key}`}
                      onClick={() => setSegKey(s.key)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${segKey === s.key ? "bg-riq-primary text-white" : "border border-riq-border text-neutral-600 hover:border-riq-primary"}`}
                    >
                      {s.label}
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${segKey === s.key ? "bg-white/25" : "bg-secondary text-riq-primary"}`}>{s.count}</span>
                    </button>
                  ))}
                </div>
              )}
              {activeSeg && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="email-segment-count">
                  <Users size={14} weight="duotone" className="text-riq-primary" />
                  This will email <b className="text-riq-primary">{groupCount}</b> user{groupCount === 1 ? "" : "s"}.
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1.5">To</div>
              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto rounded-xl border border-riq-border p-2">
                {recipients.slice(0, 40).map((r) => (
                  <span key={r.user_id} className="rounded-full bg-secondary px-2 py-0.5 text-xs text-riq-primary">{r.email || r.name}</span>
                ))}
                {recipients.length > 40 && <span className="text-xs text-muted-foreground self-center">+{recipients.length - 40} more</span>}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1.5">Template</div>
            <div className="flex flex-wrap gap-1.5">
              {TEMPLATES.map((t) => (
                <button key={t.k} data-testid={`email-tpl-${t.k}`} onClick={() => applyTemplate(t.k)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${tpl === t.k ? "bg-riq-primary text-white" : "border border-riq-border text-neutral-600 hover:border-riq-primary"}`}>
                  {t.l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1.5">Subject</div>
            <input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" className="riq-input w-full" />
          </div>

          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1.5">Message</div>
            <textarea data-testid="email-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={7}
              placeholder="Write your message… (blank lines start a new paragraph)" className="riq-input w-full resize-y" />
          </div>

          {confirming ? (
            <div className="rounded-xl border border-riq-gold/50 bg-riq-gold/10 p-3 space-y-3" data-testid="email-group-confirm">
              <div className="flex items-start gap-2 text-sm text-[#8a5a00]">
                <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
                <span>You're about to email <b>{groupCount}</b> user{groupCount === 1 ? "" : "s"} in <b>{activeSeg?.label}</b>. This can't be undone.</span>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setConfirming(false)} className="rounded-full border border-riq-border px-4 py-2 text-sm font-medium text-neutral-600 hover:border-riq-primary">Back</button>
                <button data-testid="email-group-confirm-btn" onClick={confirmGroupSend} disabled={sending}
                  className="flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-5 py-2 text-sm font-semibold disabled:opacity-50">
                  <PaperPlaneTilt size={15} weight="bold" /> {sending ? "Sending…" : `Send to ${groupCount}`}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={onClose} className="flex items-center gap-1.5 rounded-full border border-riq-border px-4 py-2 text-sm font-medium text-neutral-600 hover:border-riq-primary">
                <X size={15} weight="bold" /> Cancel
              </button>
              <button data-testid="email-send-btn" onClick={onSendClick} disabled={sending}
                className="flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-5 py-2 text-sm font-semibold disabled:opacity-50">
                <PaperPlaneTilt size={15} weight="bold" /> {sending ? "Sending…" : groupMode ? "Review & send" : "Send"}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
